import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Button, CircularProgress, TextField, Typography, Paper, Alert,
  FormControl, FormControlLabel, InputLabel, Select, MenuItem, Switch,
  Divider, Grid, IconButton, Card, CardContent, CardActions, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle, Slider, Tooltip,
  LinearProgress, Collapse
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import apiService from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const CONSULTATION_FORMATS = [
  { value: 'structured_json', label: 'Structured JSON' },
  { value: 'narrative', label: 'Narrative' },
  { value: 'recommendations_list', label: 'Recommendations List' }
];

const MODALITIES = [
  { value: 'cbt', label: 'CBT' },
  { value: 'dbt', label: 'DBT' },
  { value: 'mbt', label: 'MBT' },
  { value: 'act', label: 'ACT' },
  { value: 'emdr', label: 'EMDR' },
  { value: 'motivational_interviewing', label: 'Motivational Interviewing' },
  { value: 'psychoeducation', label: 'Psychoeducation' },
  { value: 'integrative', label: 'Integrative' },
  { value: 'other', label: 'Other' }
];

const LLM_PROVIDERS = [
  { value: 'default', label: 'Default' },
  { value: 'vertex_ai', label: 'Vertex AI' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' }
];

const DOMAIN_OPTIONS = [
  'trauma',
  'eating_disorders',
  'substance_use',
  'anxiety',
  'depression',
  'personality_disorders',
  'psychosis',
  'grief_and_loss',
  'ocd',
  'ptsd',
  'self_harm',
  'suicidality',
  'child_and_adolescent',
  'relationship',
  'other'
];

const EMPTY_FORM = {
  key: '',
  name: '',
  domain: '',
  description: '',
  systemPrompt: '',
  consultationFormat: 'structured_json',
  triggerPatterns: {
    keywords: [],
    themes: [],
    assessmentFlags: []
  },
  modality: 'integrative',
  llmProvider: {
    provider: 'default',
    model: '',
    temperature: 0.3,
    maxTokens: 2000
  },
  isActive: true,
  displayOrder: 0
};

function generateKey(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function SpecialistModuleManager({ token }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});

  // Chip input state
  const [keywordInput, setKeywordInput] = useState('');
  const [themeInput, setThemeInput] = useState('');
  const [flagInput, setFlagInput] = useState('');
  const [llmConfig, setLlmConfig] = useState(null);

  // AI Generation state
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateInstructions, setGenerateInstructions] = useState('');
  const [generateFiles, setGenerateFiles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');

  const headers = { 'x-auth-token': token };

  const fetchModules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/specialist-modules`, { headers });
      const data = response.data;
      setModules(Array.isArray(data) ? data : data.modules || data.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching specialist modules:', err);
      setError(err.response?.data?.error || err.response?.data?.msg || 'Failed to load specialist modules');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchModules();
    // Fetch LLM config for provider/model dropdowns
    const fetchLlmConfig = async () => {
      try {
        const data = await apiService.getLLMConfiguration(token);
        setLlmConfig(data);
      } catch (err) { console.error('Error fetching LLM config:', err); }
    };
    if (token) fetchLlmConfig();
  }, [fetchModules]);

  // Auto-clear success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.domain.trim()) errors.domain = 'Domain is required';
    if (!formData.systemPrompt.trim()) errors.systemPrompt = 'System prompt is required';
    if (!formData.key.trim()) errors.key = 'Key is required';
    if (!isEditing && modules.some(m => m.key === formData.key)) {
      errors.key = 'A module with this key already exists';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetGenerateState = () => {
    setGenerateOpen(false);
    setGenerateInstructions('');
    setGenerateFiles([]);
    setGenerating(false);
    setGenerateError('');
  };

  const handleOpenCreate = () => {
    setFormData({ ...EMPTY_FORM, triggerPatterns: { keywords: [], themes: [], assessmentFlags: [] }, llmProvider: { ...EMPTY_FORM.llmProvider } });
    setIsEditing(false);
    setFormErrors({});
    setKeywordInput('');
    setThemeInput('');
    setFlagInput('');
    resetGenerateState();
    setDialogOpen(true);
  };

  const handleOpenEdit = (mod) => {
    setFormData({
      key: mod.key || '',
      name: mod.name || '',
      domain: mod.domain || '',
      description: mod.description || '',
      systemPrompt: mod.systemPrompt || '',
      consultationFormat: mod.consultationFormat || 'structured_json',
      triggerPatterns: {
        keywords: [...(mod.triggerPatterns?.keywords || [])],
        themes: [...(mod.triggerPatterns?.themes || [])],
        assessmentFlags: [...(mod.triggerPatterns?.assessmentFlags || [])]
      },
      modality: mod.modality || 'integrative',
      llmProvider: {
        provider: mod.llmProvider?.provider || 'default',
        model: mod.llmProvider?.model || '',
        temperature: mod.llmProvider?.temperature ?? 0.3,
        maxTokens: mod.llmProvider?.maxTokens ?? 2000
      },
      isActive: mod.isActive !== false,
      displayOrder: mod.displayOrder || 0
    });
    setIsEditing(true);
    setFormErrors({});
    setKeywordInput('');
    setThemeInput('');
    setFlagInput('');
    resetGenerateState();
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setFormErrors({});
    resetGenerateState();
  };

  const handleGenerateFileAdd = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setGenerateFiles(prev => {
      const combined = [...prev, ...newFiles];
      return combined.slice(0, 5); // max 5 files
    });
    e.target.value = ''; // reset input
  };

  const handleGenerateFileRemove = (index) => {
    setGenerateFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!generateInstructions.trim()) {
      setGenerateError('Please provide instructions for the AI.');
      return;
    }

    setGenerating(true);
    setGenerateError('');

    try {
      const formDataPayload = new FormData();
      formDataPayload.append('instructions', generateInstructions.trim());
      if (formData.name) formDataPayload.append('moduleName', formData.name);
      if (formData.domain) formDataPayload.append('domain', formData.domain);
      if (formData.modality) formDataPayload.append('modality', formData.modality);

      for (const file of generateFiles) {
        formDataPayload.append('referenceFiles', file);
      }

      const response = await axios.post(
        `${API_BASE_URL}/admin/specialist-modules/generate-prompt`,
        formDataPayload,
        {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000 // 2 min timeout for LLM generation
        }
      );

      if (response.data?.systemPrompt) {
        const data = response.data;

        // Single atomic update for all generated fields
        setFormData(prev => ({
          ...prev,
          systemPrompt: data.systemPrompt,
          triggerPatterns: {
            keywords: data.triggerPatterns?.keywords?.length ? data.triggerPatterns.keywords : prev.triggerPatterns.keywords,
            themes: data.triggerPatterns?.themes?.length ? data.triggerPatterns.themes : prev.triggerPatterns.themes,
            assessmentFlags: data.triggerPatterns?.assessmentFlags?.length ? data.triggerPatterns.assessmentFlags : prev.triggerPatterns.assessmentFlags
          },
          description: data.description || prev.description
        }));
        // Clear validation error for systemPrompt
        if (formErrors.systemPrompt) {
          setFormErrors(prev => ({ ...prev, systemPrompt: '' }));
        }

        setGenerateOpen(false);
        setGenerateInstructions('');
        setGenerateFiles([]);
      }
    } catch (err) {
      console.error('Error generating prompt:', err);
      setGenerateError(
        err.response?.data?.msg || err.response?.data?.error || 'Failed to generate prompt. Please try again.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNameChange = (value) => {
    handleFieldChange('name', value);
    if (!isEditing) {
      handleFieldChange('key', generateKey(value));
    }
  };

  const handleLlmChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      llmProvider: { ...prev.llmProvider, [field]: value }
    }));
  };

  // Chip input handlers
  const handleChipKeyDown = (e, type, inputSetter) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const val = e.target.value.trim();
      if (!formData.triggerPatterns[type].includes(val)) {
        setFormData(prev => ({
          ...prev,
          triggerPatterns: {
            ...prev.triggerPatterns,
            [type]: [...prev.triggerPatterns[type], val]
          }
        }));
      }
      inputSetter('');
    }
  };

  const handleChipDelete = (type, index) => {
    setFormData(prev => ({
      ...prev,
      triggerPatterns: {
        ...prev.triggerPatterns,
        [type]: prev.triggerPatterns[type].filter((_, i) => i !== index)
      }
    }));
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...formData,
        llmProvider: {
          ...formData.llmProvider,
          temperature: parseFloat(formData.llmProvider.temperature),
          maxTokens: parseInt(formData.llmProvider.maxTokens, 10)
        },
        displayOrder: parseInt(formData.displayOrder, 10) || 0
      };

      if (isEditing) {
        await axios.put(`${API_BASE_URL}/admin/specialist-modules/${formData.key}`, payload, { headers });
        setSuccess(`Module "${formData.name}" updated successfully`);
      } else {
        await axios.post(`${API_BASE_URL}/admin/specialist-modules`, payload, { headers });
        setSuccess(`Module "${formData.name}" created successfully`);
      }

      setDialogOpen(false);
      fetchModules();
    } catch (err) {
      console.error('Error saving specialist module:', err);
      setError(err.response?.data?.error || err.response?.data?.msg || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (mod) => {
    if (!window.confirm(`Are you sure you want to deactivate "${mod.name}"? This will not delete data but the module will no longer be available.`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/admin/specialist-modules/${mod.key}`, { headers });
      setSuccess(`Module "${mod.name}" deactivated successfully`);
      fetchModules();
    } catch (err) {
      console.error('Error deactivating specialist module:', err);
      setError(err.response?.data?.error || err.response?.data?.msg || 'Failed to deactivate module');
    }
  };

  const getModalityLabel = (value) => {
    const found = MODALITIES.find(m => m.value === value);
    return found ? found.label : value;
  };

  const getFormatLabel = (value) => {
    const found = CONSULTATION_FORMATS.find(f => f.value === value);
    return found ? found.label : value;
  };

  const getProviderLabel = (value) => {
    const found = LLM_PROVIDERS.find(p => p.value === value);
    return found ? found.label : value;
  };

  const getDomainLabel = (domain) => {
    return (domain || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Render chip input helper
  const renderChipInput = (label, type, inputValue, inputSetter, placeholder) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>{label}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, minHeight: 32 }}>
        {formData.triggerPatterns[type].map((item, index) => (
          <Chip
            key={index}
            label={item}
            size="small"
            onDelete={() => handleChipDelete(type, index)}
            color="primary"
            variant="outlined"
          />
        ))}
      </Box>
      <TextField
        size="small"
        fullWidth
        placeholder={placeholder || `Type and press Enter to add`}
        value={inputValue}
        onChange={(e) => inputSetter(e.target.value)}
        onKeyDown={(e) => handleChipKeyDown(e, type, inputSetter)}
        helperText="Press Enter to add"
      />
    </Box>
  );

  if (loading && modules.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading specialist modules...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">Specialist Module Manager</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage Gen 2 specialist consultation modules for clinical domains
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          New Module
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Module Cards */}
      {modules.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            No specialist modules found. Create one to get started.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Create First Module
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {modules.map((mod) => (
            <Grid item xs={12} sm={6} md={4} key={mod.key || mod._id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: mod.isActive === false ? 0.6 : 1,
                  borderLeft: mod.isActive === false ? '4px solid #bdbdbd' : '4px solid #1976d2'
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {mod.name}
                    </Typography>
                    <Chip
                      label={mod.isActive === false ? 'Inactive' : 'Active'}
                      color={mod.isActive === false ? 'default' : 'success'}
                      size="small"
                    />
                  </Box>

                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', mb: 1 }}>
                    {mod.key}
                  </Typography>

                  {mod.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {mod.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    <Chip label={getDomainLabel(mod.domain)} size="small" color="primary" variant="outlined" />
                    <Chip label={getModalityLabel(mod.modality)} size="small" variant="outlined" />
                    <Chip label={getFormatLabel(mod.consultationFormat)} size="small" variant="outlined" />
                  </Box>

                  {mod.llmProvider?.provider && mod.llmProvider.provider !== 'default' && (
                    <Typography variant="caption" color="text.secondary">
                      LLM: {getProviderLabel(mod.llmProvider.provider)}
                      {mod.llmProvider.model ? ` (${mod.llmProvider.model})` : ''}
                    </Typography>
                  )}

                  {/* Trigger pattern summary */}
                  {(mod.triggerPatterns?.keywords?.length > 0 || mod.triggerPatterns?.themes?.length > 0) && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Triggers:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(mod.triggerPatterns?.keywords || []).slice(0, 3).map((kw, i) => (
                          <Chip key={`kw-${i}`} label={kw} size="small" sx={{ fontSize: '0.7rem' }} />
                        ))}
                        {(mod.triggerPatterns?.themes || []).slice(0, 2).map((th, i) => (
                          <Chip key={`th-${i}`} label={th} size="small" color="secondary" sx={{ fontSize: '0.7rem' }} />
                        ))}
                        {((mod.triggerPatterns?.keywords?.length || 0) + (mod.triggerPatterns?.themes?.length || 0)) > 5 && (
                          <Chip label="..." size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>

                <Divider />

                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Tooltip title="Edit module">
                    <IconButton size="small" onClick={() => handleOpenEdit(mod)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {mod.isActive !== false && (
                    <Tooltip title="Deactivate module">
                      <IconButton size="small" color="error" onClick={() => handleDeactivate(mod)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>
          {isEditing ? `Edit Module: ${formData.name}` : 'Create New Specialist Module'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>

            {/* Section: Basic Information */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Basic Information</Typography>

            <TextField
              fullWidth
              label="Module Name"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
            />

            <TextField
              fullWidth
              label="Key"
              value={formData.key}
              onChange={(e) => handleFieldChange('key', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              disabled={isEditing}
              error={!!formErrors.key}
              helperText={formErrors.key || (isEditing ? 'Key cannot be changed after creation' : 'Auto-generated from name (lowercase, hyphens)')}
              required
              InputProps={{
                sx: { fontFamily: 'monospace' }
              }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!formErrors.domain}>
                  <InputLabel>Domain</InputLabel>
                  <Select
                    value={formData.domain}
                    onChange={(e) => handleFieldChange('domain', e.target.value)}
                    label="Domain"
                  >
                    {DOMAIN_OPTIONS.map((d) => (
                      <MenuItem key={d} value={d}>{getDomainLabel(d)}</MenuItem>
                    ))}
                  </Select>
                  {formErrors.domain && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {formErrors.domain}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Modality</InputLabel>
                  <Select
                    value={formData.modality}
                    onChange={(e) => handleFieldChange('modality', e.target.value)}
                    label="Modality"
                  >
                    {MODALITIES.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              multiline
              rows={2}
              placeholder="Brief description of this specialist module's purpose"
            />

            <FormControl fullWidth>
              <InputLabel>Consultation Format</InputLabel>
              <Select
                value={formData.consultationFormat}
                onChange={(e) => handleFieldChange('consultationFormat', e.target.value)}
                label="Consultation Format"
              >
                {CONSULTATION_FORMATS.map((f) => (
                  <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  label="Display Order"
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => handleFieldChange('displayOrder', e.target.value)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />

            {/* Section: System Prompt */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>System Prompt</Typography>

            {/* AI-Assisted Generation Panel */}
            <Paper
              variant="outlined"
              sx={{
                border: '1px dashed',
                borderColor: generateOpen ? 'primary.main' : 'divider',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  cursor: 'pointer',
                  bgcolor: generateOpen ? 'primary.50' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => !generating && setGenerateOpen(!generateOpen)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesomeIcon fontSize="small" color="primary" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    AI-Assisted Generation
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    — Generate a comprehensive system prompt from instructions
                  </Typography>
                </Box>
                {generateOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Box>

              <Collapse in={generateOpen}>
                <Box sx={{ px: 2, pb: 2 }}>
                  {formData.systemPrompt.trim() && (
                    <Alert severity="warning" sx={{ mb: 1.5, mt: 0.5 }} variant="outlined">
                      Generating will replace the existing system prompt below.
                    </Alert>
                  )}

                  {generateError && (
                    <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setGenerateError('')}>
                      {generateError}
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    label="Instructions for AI"
                    placeholder="e.g., Create a trauma-informed care specialist for complex PTSD in young adults, incorporating prolonged exposure and CPT frameworks with emphasis on dissociation screening"
                    value={generateInstructions}
                    onChange={(e) => setGenerateInstructions(e.target.value)}
                    multiline
                    rows={4}
                    disabled={generating}
                    sx={{ mb: 1.5 }}
                  />

                  {/* File Upload */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      component="label"
                      disabled={generating || generateFiles.length >= 5}
                    >
                      Attach Reference Files
                      <input
                        type="file"
                        hidden
                        multiple
                        accept=".txt,.md,.pdf"
                        onChange={handleGenerateFileAdd}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      .txt, .md, .pdf (max 5 files, 5MB each)
                    </Typography>
                  </Box>

                  {generateFiles.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                      {generateFiles.map((file, i) => (
                        <Chip
                          key={i}
                          label={file.name}
                          size="small"
                          icon={<AttachFileIcon />}
                          onDelete={generating ? undefined : () => handleGenerateFileRemove(i)}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}

                  {generating && (
                    <Box sx={{ mb: 1.5 }}>
                      <LinearProgress sx={{ mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        Generating specialist module prompt... This typically takes 15-30 seconds.
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={handleGenerate}
                    disabled={generating || !generateInstructions.trim()}
                    size="small"
                  >
                    {generating ? 'Generating...' : 'Generate System Prompt'}
                  </Button>
                </Box>
              </Collapse>
            </Paper>

            <TextField
              fullWidth
              label="System Prompt"
              value={formData.systemPrompt}
              onChange={(e) => handleFieldChange('systemPrompt', e.target.value)}
              error={!!formErrors.systemPrompt}
              helperText={formErrors.systemPrompt || 'The LLM prompt defining how this specialist module conducts consultations'}
              multiline
              rows={10}
              required
              InputProps={{
                sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
              }}
            />

            <Divider sx={{ my: 1 }} />

            {/* Section: Trigger Patterns */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Trigger Patterns</Typography>
              <Tooltip title="Keywords, themes, and assessment flags that trigger this specialist module during clinical sessions">
                <InfoIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>

            {renderChipInput('Keywords', 'keywords', keywordInput, setKeywordInput, 'e.g., flashback, dissociation, trauma')}
            {renderChipInput('Themes', 'themes', themeInput, setThemeInput, 'e.g., avoidance, hyperarousal, emotional_dysregulation')}
            {renderChipInput('Assessment Flags', 'assessmentFlags', flagInput, setFlagInput, 'e.g., pcl5_high, phq9_severe, gad7_moderate')}

            <Divider sx={{ my: 1 }} />

            {/* Section: LLM Provider Configuration */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>LLM Provider Configuration</Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Providers and models are configured in the LLM Configuration page.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={formData.llmProvider.provider}
                    onChange={(e) => {
                      handleLlmChange('provider', e.target.value);
                      handleLlmChange('model', '');
                    }}
                    label="Provider"
                  >
                    <MenuItem value="default">Default (use platform primary)</MenuItem>
                    {llmConfig?.availableModels && [...new Set(llmConfig.availableModels.filter(m => m.isActive).map(m => m.provider))].map(p => (
                      <MenuItem key={p} value={p}>{p === 'vertex_ai' ? 'Vertex AI' : p === 'gemini' ? 'Gemini' : p === 'openai' ? 'OpenAI' : p === 'anthropic' ? 'Anthropic' : p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={formData.llmProvider.model || ''}
                    onChange={(e) => handleLlmChange('model', e.target.value)}
                    label="Model"
                    disabled={formData.llmProvider.provider === 'default'}
                  >
                    <MenuItem value="">Provider default</MenuItem>
                    {llmConfig?.availableModels && llmConfig.availableModels
                      .filter(m => m.isActive && (formData.llmProvider.provider === 'default' || m.provider === formData.llmProvider.provider))
                      .map(m => (
                        <MenuItem key={`${m.provider}-${m.id}`} value={m.id}>{m.displayName || m.id}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ px: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Temperature: {formData.llmProvider.temperature}
              </Typography>
              <Slider
                value={formData.llmProvider.temperature}
                onChange={(_, value) => handleLlmChange('temperature', value)}
                min={0}
                max={2}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.3, label: '0.3' },
                  { value: 1, label: '1' },
                  { value: 2, label: '2' }
                ]}
                valueLabelDisplay="auto"
              />
            </Box>

            <TextField
              label="Max Tokens"
              type="number"
              value={formData.llmProvider.maxTokens}
              onChange={(e) => handleLlmChange('maxTokens', e.target.value)}
              inputProps={{ min: 100, max: 32000, step: 100 }}
              helperText="Maximum tokens for LLM response (default: 2000)"
              sx={{ maxWidth: 300 }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Saving...' : (isEditing ? 'Update Module' : 'Create Module')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SpecialistModuleManager;
